import { supabase } from '../lib/supabase';

export const authService = {
  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    try {
      if (!supabase || !supabase?.auth) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }

      if (!data || !data?.user) {
        throw new Error('Sign in failed: No user data returned');
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign up with email, password, and profile data
   * Profile data is passed in options.data and becomes raw_user_meta_data
   * Trigger function reads this and creates user_profiles row automatically
   */
  async signUp(email, password, fullName, role = 'user') {
    try {
      if (!supabase || !supabase?.auth) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
            avatar_url: ''
          }
        }
      });
      
      if (error) {
        console.error('Sign up error:', error);
        throw error;
      }

      if (!data || !data?.user) {
        throw new Error('Sign up failed: No user data returned');
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign in with Google OAuth
   * Accepts optional role parameter that will be stored and used after OAuth redirect
   */
  async signInWithGoogle(role = 'user') {
    try {
      if (!supabase || !supabase?.auth) {
        throw new Error('Supabase client not initialized');
      }

      // Store selected role in sessionStorage for OAuth callback
      if (role) {
        sessionStorage.setItem('pendingUserRole', role);
      }

      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      if (error) {
        console.error('Google sign in error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user role after OAuth signup
   * Called after OAuth redirect to set the pending role
   */
  async updateUserRole(userId, role) {
    try {
      if (!supabase || !supabase?.from) {
        throw new Error('Supabase client not initialized');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({
          role: role,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();
      
      if (error) {
        console.error('Update user role error:', error);
        throw error;
      }
      
      // Clear pending role from sessionStorage
      sessionStorage.removeItem('pendingUserRole');
      
      return { data, error: null };
    } catch (error) {
      console.error('Update user role error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get and apply pending OAuth role
   * Should be called after OAuth redirect completes
   */
  async applyPendingOAuthRole(userId) {
    try {
      const pendingRole = sessionStorage.getItem('pendingUserRole');
      
      if (!pendingRole || !userId) {
        return { data: null, error: null };
      }

      // Validate role
      if (!['user', 'helper', 'admin']?.includes(pendingRole)) {
        sessionStorage.removeItem('pendingUserRole');
        return { data: null, error: { message: 'Invalid role' } };
      }

      // Update user profile with pending role
      const result = await this.updateUserRole(userId, pendingRole);
      return result;
    } catch (error) {
      console.error('Apply pending OAuth role error:', error);
      sessionStorage.removeItem('pendingUserRole');
      return { data: null, error };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      if (!supabase || !supabase?.auth) {
        throw new Error('Supabase client not initialized');
      }

      const { error } = await supabase?.auth?.signOut();
      if (error) {
        console.error('Sign out error:', error);
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  },

  /**
   * Get current user session
   */
  async getSession() {
    try {
      if (!supabase || !supabase?.auth) {
        throw new Error('Supabase client not initialized');
      }

      const { data: { session }, error } = await supabase?.auth?.getSession();
      if (error) {
        console.error('Get session error:', error);
        throw error;
      }

      return { session, error: null };
    } catch (error) {
      console.error('Get session error:', error);
      return { session: null, error };
    }
  },

  /**
   * Get current user profile from user_profiles table
   */
  async getUserProfile(userId) {
    try {
      if (!supabase || !supabase?.from) {
        throw new Error('Supabase client not initialized');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) {
        console.error('Get user profile error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Get user profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(userId, updates) {
    try {
      if (!supabase || !supabase?.from) {
        throw new Error('Supabase client not initialized');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: updates?.fullName,
          avatar_url: updates?.avatarUrl,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();
      
      if (error) {
        console.error('Update profile error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { data: null, error };
    }
  }
};